'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@benzinga/core-ui';

export type IamOrderCompleteDetail = Record<string, unknown> & {
  amount?: number;
};

export type PremiumCheckoutCloseResult = {
  completed: boolean;
};

export type PremiumCheckoutIframeModalProps = {
  iframeSrc: string;
  onClose: (result?: PremiumCheckoutCloseResult) => void;
  onOrderComplete?: (detail: IamOrderCompleteDetail) => void;
  open: boolean;
  title?: string;
};

const MODAL_WIDTH = 'min(700px, calc(100vw - 24px))';
const IFRAME_VIEWPORT_OFFSET = 96;
const ORDER_COMPLETE_DISMISS_MS = 2500;

export const PremiumCheckoutIframeModal: React.FC<PremiumCheckoutIframeModalProps> = ({
  iframeSrc,
  onClose,
  onOrderComplete,
  open,
  title = 'Benzinga Edge checkout',
}) => {
  const onOrderCompleteRef = useRef(onOrderComplete);
  const onCloseRef = useRef(onClose);
  const orderCompleteHandledRef = useRef(false);
  const [orderComplete, setOrderComplete] = useState(false);
  onOrderCompleteRef.current = onOrderComplete;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (open) return;
    setOrderComplete(false);
    orderCompleteHandledRef.current = false;
  }, [open]);

  const handleOrderComplete = useCallback((detail: IamOrderCompleteDetail) => {
    if (orderCompleteHandledRef.current) return;
    orderCompleteHandledRef.current = true;
    setOrderComplete(true);
    onOrderCompleteRef.current?.(detail);
  }, []);

  useEffect(() => {
    if (!orderComplete) return;
    const timer = window.setTimeout(() => {
      onCloseRef.current({ completed: true });
    }, ORDER_COMPLETE_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [orderComplete]);

  const handleClose = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onClose({ completed: false });
    },
    [onClose],
  );

  useEffect(() => {
    if (!open || orderComplete) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose({ completed: false });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, orderComplete]);

  useEffect(() => {
    if (!open) return;

    let expectedOrigin: string | null = null;
    try {
      expectedOrigin = new URL(iframeSrc).origin;
    } catch {
      expectedOrigin = null;
    }

    const handleNativeOrderComplete = (event: Event) => {
      const customEvent = event as CustomEvent<IamOrderCompleteDetail>;
      const detail = customEvent.detail ?? ({} as IamOrderCompleteDetail);
      handleOrderComplete(detail);
    };

    const handleMessage = (event: MessageEvent) => {
      if (!expectedOrigin || event.origin !== expectedOrigin) return;
      const payload = event.data;
      if (!payload || typeof payload !== 'object') return;
      const typed = payload as { detail?: IamOrderCompleteDetail; type?: string };
      if (typed.type !== 'iam_order_complete') return;
      const detail = typed.detail ?? (payload as IamOrderCompleteDetail);
      handleOrderComplete(detail);
    };

    document.addEventListener('iam_order_complete', handleNativeOrderComplete);
    window.addEventListener('message', handleMessage);
    return () => {
      document.removeEventListener('iam_order_complete', handleNativeOrderComplete);
      window.removeEventListener('message', handleMessage);
    };
  }, [handleOrderComplete, iframeSrc, open]);

  const handleModalClose = useCallback(() => {
    if (orderComplete) return;
    onClose({ completed: false });
  }, [onClose, orderComplete]);

  return (
    <Modal
      containerStyle={{
        background: '#0c1c33',
        backgroundClip: 'padding-box',
        border: 'none',
        borderColor: 'transparent',
        borderRadius: '8px',
        borderStyle: 'none',
        borderWidth: 0,
        boxShadow: 'none',
        outline: 'none',
        overflow: 'hidden',
        width: MODAL_WIDTH,
      }}
      fullScreen
      hideTitle
      onClose={handleModalClose}
      size={{
        manual: {
          height: 'auto',
          maxHeight: 'calc(100dvh - 24px)',
          maxWidth: MODAL_WIDTH,
          width: MODAL_WIDTH,
        },
      }}
      title={title}
      visible={open}
      zIndex={60}
    >
      <div className="flex flex-col">
        <header className="shrink-0 border-b border-white/10 px-4 pb-3 pt-3 sm:px-5">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-semibold tracking-tight text-white sm:text-lg" id="modalTitle">
                {title}
              </p>
              <p className="mt-0.5 truncate text-xs text-slate-400 sm:text-sm">Secure checkout on benzinga.com</p>
            </div>
            {!orderComplete && (
              <button
                aria-label="Close"
                className="-mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition-colors hover:bg-white/12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400/70"
                onClick={handleClose}
                type="button"
              >
                <svg
                  aria-hidden
                  className="block text-white"
                  fill="none"
                  height="20"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  width="20"
                >
                  <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-hidden rounded-b-lg bg-[#0c1c33]">
          <iframe
            allow="payment *"
            className="block w-full border-0 bg-[#0c1c33] outline-none"
            src={iframeSrc}
            style={{
              border: 0,
              borderRadius: '0 0 8px 8px',
              display: 'block',
              height: `calc(100dvh - ${IFRAME_VIEWPORT_OFFSET}px)`,
              margin: 0,
              maxHeight: `calc(100dvh - ${IFRAME_VIEWPORT_OFFSET}px)`,
              padding: 0,
              verticalAlign: 'top',
              width: '100%',
            }}
            title={title}
          />
        </div>
      </div>
    </Modal>
  );
};
